import {
  Component,
  JSX,
  Show,
  createSignal,
  createContext,
  useContext,
  ParentComponent,
} from "solid-js";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | JSX.Element;
  children: JSX.Element;
  actions?: JSX.Element;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  class?: string;
}

const sizeClasses: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-full w-11/12",
};

export const Modal: Component<ModalProps> = (props) => {
  const size = () => props.size ?? "md";
  const closeOnBackdrop = () => props.closeOnBackdrop !== false;
  const showCloseButton = () => props.showCloseButton !== false;

  const handleBackdropClick = () => {
    if (closeOnBackdrop()) {
      props.onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && props.closeOnEscape !== false) {
      props.onClose();
    }
  };

  return (
    <Show when={props.isOpen}>
      <div class="modal modal-open" onKeyDown={handleKeyDown} tabIndex={-1}>
        <div class={`modal-box ${sizeClasses[size()]} ${props.class ?? ""}`}>
          <Show when={showCloseButton()}>
            <button
              class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={props.onClose}
              aria-label="Close">
              \u2715
            </button>
          </Show>
          <Show when={props.title}>
            <h3 class="font-bold text-lg mb-4">{props.title}</h3>
          </Show>
          <div class="py-2">{props.children}</div>
          <Show when={props.actions}>
            <div class="modal-action">{props.actions}</div>
          </Show>
        </div>
        <div class="modal-backdrop bg-black/50" onClick={handleBackdropClick}></div>
      </div>
    </Show>
  );
};

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | JSX.Element;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  loading?: boolean;
}

const confirmVariants: Record<string, string> = {
  danger: "btn-error",
  warning: "btn-warning",
  info: "btn-info",
};

export const ConfirmModal: Component<ConfirmModalProps> = (props) => {
  const variant = () => props.variant ?? "danger";
  const confirmText = () => props.confirmText ?? "Confirm";
  const cancelText = () => props.cancelText ?? "Cancel";

  const handleConfirm = async () => {
    await props.onConfirm();
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={props.title}
      size="sm"
      actions={
        <>
          <button class="btn btn-ghost" onClick={props.onClose} disabled={props.loading}>
            {cancelText()}
          </button>
          <button
            class={`btn ${confirmVariants[variant()]}`}
            onClick={handleConfirm}
            disabled={props.loading}>
            <Show when={props.loading}>
              <span class="loading loading-spinner loading-sm"></span>
            </Show>
            {confirmText()}
          </button>
        </>
      }>
      <div class="text-base-content/80">{props.message}</div>
    </Modal>
  );
};

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | JSX.Element;
  variant?: "success" | "error" | "warning" | "info";
  buttonText?: string;
}

const alertIcons: Record<string, string> = {
  success: "\u2713",
  error: "\u2717",
  warning: "\u26A0",
  info: "\u2139",
};

const alertColors: Record<string, string> = {
  success: "text-success",
  error: "text-error",
  warning: "text-warning",
  info: "text-info",
};

export const AlertModal: Component<AlertModalProps> = (props) => {
  const variant = () => props.variant ?? "info";
  const buttonText = () => props.buttonText ?? "OK";

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="sm"
      actions={
        <button class="btn btn-primary" onClick={props.onClose}>
          {buttonText()}
        </button>
      }>
      <div class="text-center">
        <div class={`text-5xl mb-4 ${alertColors[variant()]}`}>{alertIcons[variant()]}</div>
        <h3 class="font-bold text-lg mb-2">{props.title}</h3>
        <p class="text-base-content/80">{props.message}</p>
      </div>
    </Modal>
  );
};

// Modal Manager Context for global modal management
interface ModalState {
  confirm: (options: Omit<ConfirmModalProps, "isOpen" | "onClose">) => Promise<boolean>;
  alert: (options: Omit<AlertModalProps, "isOpen" | "onClose">) => Promise<void>;
}

const ModalContext = createContext<ModalState>();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

export const ModalProvider: ParentComponent = (props) => {
  const [confirmState, setConfirmState] = createSignal<{
    options: Omit<ConfirmModalProps, "isOpen" | "onClose" | "onConfirm">;
    resolve: (value: boolean) => void;
  } | null>(null);

  const [alertState, setAlertState] = createSignal<{
    options: Omit<AlertModalProps, "isOpen" | "onClose">;
    resolve: () => void;
  } | null>(null);

  const modalState: ModalState = {
    confirm: (options) => {
      return new Promise((resolve) => {
        setConfirmState({ options, resolve });
      });
    },
    alert: (options) => {
      return new Promise((resolve) => {
        setAlertState({ options, resolve });
      });
    },
  };

  const handleConfirmClose = () => {
    confirmState()?.resolve(false);
    setConfirmState(null);
  };

  const handleConfirmConfirm = () => {
    confirmState()?.resolve(true);
    setConfirmState(null);
  };

  const handleAlertClose = () => {
    alertState()?.resolve();
    setAlertState(null);
  };

  return (
    <ModalContext.Provider value={modalState}>
      {props.children}
      <Show when={confirmState()}>
        <ConfirmModal
          isOpen={true}
          onClose={handleConfirmClose}
          onConfirm={handleConfirmConfirm}
          {...confirmState()!.options}
        />
      </Show>
      <Show when={alertState()}>
        <AlertModal isOpen={true} onClose={handleAlertClose} {...alertState()!.options} />
      </Show>
    </ModalContext.Provider>
  );
};
